/**
 * The orbital background plate.
 *
 * A 10.5s loop of Earth's night side drifting toward the viewer at constant
 * altitude. The generator handed back a 12s clip whose first and last frames
 * were close but not equal, so a hard `loop` popped once every pass; what ships
 * here is that clip with its tail cross-dissolved back over its head, which
 * costs 1.5s of runtime and removes the seam.
 *
 * WHY TWO ENCODES. The source was HEVC Main 10 — fine in Safari, and largely
 * undecodable in Chrome and Firefox, where on Windows it wants a paid extension
 * from the Microsoft Store. Shipping it as authored would have shown most
 * players a black rectangle and no error. VP9 leads for the browsers that take
 * it and H.264 backs it up, so every engine gets something it can actually
 * play.
 *
 * WHERE IT DOES NOT GO. The match screen draws a real globe: the plate would be
 * redundant behind it and would put a second decoder on the most frame-
 * sensitive screen in the game. The queue is the group lobby, which is left
 * plain on purpose.
 */

/**
 * Read once at mount rather than subscribed to. A player who flips the OS
 * setting mid-session gets the change on their next navigation, and in exchange
 * the reduced-motion path never downloads a video it has already decided not to
 * show — which `display: none` alone would not prevent.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function BackgroundPlate(): React.JSX.Element {
  // The poster is painted by CSS on `.gg-bg`, so the still case needs no child.
  if (prefersReducedMotion()) return <div className="gg-bg" aria-hidden="true" />;

  return (
    <div className="gg-bg" aria-hidden="true">
      <video autoPlay muted loop playsInline preload="auto" poster="/bg/poster.webp">
        <source src="/bg/loop_720.webm" type="video/webm" />
        <source src="/bg/loop_720.mp4" type="video/mp4" />
      </video>
    </div>
  );
}
